import { createReducer } from '@topconagriculture/frontend-utils';

import { DeliveryInLotCreation, GroupInLotCreation, LotCostItem, MovementInCreation } from './appLots.types';
import {
  addGroupToLot,
  addMovement,
  addMovementInPreview,
  addTransfersHistoryMovement,
  deleteGroupFromLot,
  deleteMovement,
  deleteTransfersHistoryMovement,
  disableReportFetching,
  editGroupInLot,
  editMovement,
  getCloseoutReport,
  getCostSummaryReport,
  getDeliveries,
  getLotCattleMovements,
  previewLot,
  reset,
  resetDeliveries,
  resetMovements,
  resetPdfData,
  resetPreviewData,
  resetTransfersHistory,
  setAutoExpandAdditionalCost,
  setIncludedCloseouts,
  setIncludedCloseoutsExisting,
  setTransfersHistoryLot,
  setTransfersHistoryLotIsEdit,
  updateMovementInPreview,
  updateTransfersHistoryMovement,
} from './appLots.actions';
import { Lot } from '../../entities/lots/lots.types';
import { CattleMovementReportItem, Report } from '../../entities/reports/reports.types';

export type AppLotsState = {
  groups: GroupInLotCreation[];
  deliveries: DeliveryInLotCreation[];
  movements: MovementInCreation[];
  closeoutPreview: {
    pdfData:
      | {
          data: Uint8Array;
        }
      | undefined;
    includedCloseouts: string[];
    includedCloseoutsExisting: string[];
  };
  transfersHistory: {
    lotPreview: Lot | undefined;
    changed: boolean;
    newMovements: MovementInCreation[];
    isEdit: boolean;
  };
  costSummaryReport: Report<LotCostItem> | null;
  disableReportFetching: boolean;
  autoExpandedAdditionalCost: string;
  costMovements: Report<CattleMovementReportItem> | null;
};

const initialState: AppLotsState = {
  groups: [],
  deliveries: [],
  movements: [],
  closeoutPreview: {
    pdfData: undefined,
    includedCloseouts: [],
    includedCloseoutsExisting: [],
  },
  transfersHistory: {
    lotPreview: undefined,
    changed: false,
    newMovements: [],
    isEdit: false,
  },
  costSummaryReport: null,
  disableReportFetching: false,
  autoExpandedAdditionalCost: '',
  costMovements: null,
};

const appLotsReducer = createReducer(initialState, {
  [addGroupToLot.type]: (state: AppLotsState, action: ReturnType<typeof addGroupToLot>) => ({
    ...state,
    groups: [
      ...state.groups,
      {
        groupId: action.payload,
        arrivalDate: new Date(),
        headCountIn: 0,
        weightIn: 0,
      },
    ],
  }),
  [deleteGroupFromLot.type]: (state: AppLotsState, action: ReturnType<typeof deleteGroupFromLot>) => ({
    ...state,
    groups: state.groups.filter((x) => x.groupId !== action.payload),
  }),
  [editGroupInLot.type]: (state: AppLotsState, action: ReturnType<typeof editGroupInLot>) => ({
    ...state,
    groups: state.groups.map((x) => (x.groupId === action.payload.groupId ? { ...x, ...action.payload } : x)),
  }),
  [reset.type]: () => ({ ...initialState }),
  [getDeliveries.done.type]: (state: AppLotsState, action: ReturnType<typeof getDeliveries.done>) => ({
    ...state,
    deliveries: action.payload.result,
  }),
  [resetDeliveries.type]: (state: AppLotsState) => ({
    ...state,
    deliveries: [],
  }),
  [addMovement.type]: (state: AppLotsState, action: ReturnType<typeof addMovement>) => ({
    ...state,
    movements: [...state.movements, action.payload],
  }),
  [editMovement.type]: (state: AppLotsState, action: ReturnType<typeof editMovement>) => ({
    ...state,
    movements: state.movements.map((x) => (x.uuid === action.payload.uuid ? { ...x, ...action.payload } : x)),
  }),
  [deleteMovement.type]: (state: AppLotsState, action: ReturnType<typeof deleteMovement>) => ({
    ...state,
    movements: state.movements.filter((x) => x.uuid !== action.payload),
  }),
  [resetMovements.type]: (state: AppLotsState) => ({
    ...state,
    movements: [],
  }),
  [getCloseoutReport.done.type]: (state: AppLotsState, action: ReturnType<typeof getCloseoutReport.done>) => ({
    ...state,
    closeoutPreview: {
      ...state.closeoutPreview,
      pdfData: {
        data: action.payload.result,
      },
    },
  }),
  [setIncludedCloseouts.type]: (state: AppLotsState, action: ReturnType<typeof setIncludedCloseouts>) => ({
    ...state,
    closeoutPreview: {
      ...state.closeoutPreview,
      includedCloseouts: action.payload,
    },
  }),
  [setIncludedCloseoutsExisting.type]: (
    state: AppLotsState,
    action: ReturnType<typeof setIncludedCloseoutsExisting>,
  ) => ({
    ...state,
    closeoutPreview: {
      ...state.closeoutPreview,
      includedCloseoutsExisting: action.payload,
    },
  }),
  [resetPreviewData.type]: (state: AppLotsState) => ({
    ...state,
    closeoutPreview: initialState.closeoutPreview,
  }),
  [resetPdfData.type]: (state) => ({
    ...state,
    closeoutPreview: {
      ...state.closeoutPreview,
      pdfData: undefined,
    },
  }),
  [setTransfersHistoryLot.type]: (state: AppLotsState, action: ReturnType<typeof setTransfersHistoryLot>) => ({
    ...state,
    transfersHistory: {
      ...state.transfersHistory,
      lotPreview: action.payload,
    },
  }),
  [previewLot.done.type]: (state: AppLotsState, action: ReturnType<typeof previewLot.done>) => ({
    ...state,
    transfersHistory: {
      ...state.transfersHistory,
      lotPreview: action.payload.result,
      changed: false,
    },
  }),
  [updateMovementInPreview.type]: (state: AppLotsState, action: ReturnType<typeof updateMovementInPreview>) => {
    const movements = (state.transfersHistory.lotPreview && state.transfersHistory.lotPreview.movements) || [];
    const updatedMovement = action.payload;
    return {
      ...state,
      transfersHistory: {
        ...state.transfersHistory,
        lotPreview: {
          ...state.transfersHistory.lotPreview,
          movements: movements.map((x) =>
            x.id === updatedMovement.id
              ? {
                  ...x,
                  ...updatedMovement,
                }
              : x,
          ),
        },
        changed: true,
      },
    };
  },
  [addMovementInPreview.type]: (state: AppLotsState, action: ReturnType<typeof addMovementInPreview>) => {
    const movements = (state.transfersHistory.lotPreview && state.transfersHistory.lotPreview.movements) || [];
    return {
      ...state,
      transfersHistory: {
        ...state.transfersHistory,
        lotPreview: {
          ...state.transfersHistory.lotPreview,
          movements: [...movements, action.payload],
        },
        changed: true,
      },
    };
  },
  [resetTransfersHistory.type]: (state: AppLotsState) => ({
    ...state,
    transfersHistory: initialState.transfersHistory,
  }),
  [addTransfersHistoryMovement.type]: (
    state: AppLotsState,
    action: ReturnType<typeof addTransfersHistoryMovement>,
  ) => ({
    ...state,
    transfersHistory: {
      ...state.transfersHistory,
      newMovements: [...state.transfersHistory.newMovements, action.payload],
    },
  }),
  [updateTransfersHistoryMovement.type]: (
    state: AppLotsState,
    action: ReturnType<typeof updateTransfersHistoryMovement>,
  ) => {
    const movements = state.transfersHistory.newMovements;
    const updatedMovement = action.payload;
    return {
      ...state,
      transfersHistory: {
        ...state.transfersHistory,
        newMovements: movements.map((x) =>
          x.uuid === updatedMovement.uuid
            ? {
                ...x,
                ...updatedMovement,
              }
            : x,
        ),
      },
    };
  },
  [deleteTransfersHistoryMovement.type]: (
    state: AppLotsState,
    action: ReturnType<typeof deleteTransfersHistoryMovement>,
  ) => ({
    ...state,
    transfersHistory: {
      ...state.transfersHistory,
      newMovements: state.transfersHistory.newMovements.filter((x) => x.uuid !== action.payload),
    },
  }),
  [setTransfersHistoryLotIsEdit.type]: (
    state: AppLotsState,
    action: ReturnType<typeof setTransfersHistoryLotIsEdit>,
  ) => ({
    ...state,
    transfersHistory: {
      ...state.transfersHistory,
      isEdit: action.payload,
    },
  }),
  [disableReportFetching.type]: (state: AppLotsState, action: ReturnType<typeof disableReportFetching>) => ({
    ...state,
    disableReportFetching: action.payload,
  }),
  [setAutoExpandAdditionalCost.type]: (
    state: AppLotsState,
    action: ReturnType<typeof setAutoExpandAdditionalCost>,
  ) => ({
    ...state,
    autoExpandedAdditionalCost: action.payload,
  }),
  [getCostSummaryReport.done.type]: (state: AppLotsState, action: ReturnType<typeof getCostSummaryReport.done>) => ({
    ...state,
    costSummaryReport: action.payload.result,
  }),
  [getLotCattleMovements.done.type]: (state: AppLotsState, action: ReturnType<typeof getLotCattleMovements.done>) => ({
    ...state,
    costMovements: action.payload.result,
  }),
});

export default appLotsReducer;
